import { isMultileg, type MultilegModel, type Security } from '@talos/kyoko';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { MULTILEG_MARKET_ACCOUNT } from '../../providers/OMSContext.types';
import { updateMultilegLegMarketAccounts } from '../../utils/multileg';

/**
 * One entry per regular multileg instrument leg.
 *
 * An empty array is meaningful and represents "All Markets".
 */
export type LegMarketAccounts = string[][];

/**
 * Shared draft-state management for editing regular multileg leg market accounts.
 *
 * Market card settings and the quick market selector both need to initialize from the
 * instrument's current leg markets, update one leg at a time, and reset unsaved edits.
 */
export function useMultilegLegMarketAccountsEditing({ security }: { security: Security | undefined }) {
  const defaultLegMarketAccounts = useMemo(() => getSecurityLegMarketAccounts(security), [security]);
  const [legMarketAccounts, setLegMarketAccounts] = useState<LegMarketAccounts>(defaultLegMarketAccounts);

  const resetLegMarketAccounts = useCallback(() => {
    setLegMarketAccounts(defaultLegMarketAccounts);
  }, [defaultLegMarketAccounts]);

  // Reset our local state if a security change comes in over the wire
  useEffect(() => {
    resetLegMarketAccounts();
  }, [resetLegMarketAccounts]);

  const onLegMarketAccountsChange = useCallback(({ legIndex, value }: { legIndex: number; value: string[] }) => {
    setLegMarketAccounts(currentLegMarketAccounts => {
      const nextLegMarketAccounts = [...currentLegMarketAccounts];
      nextLegMarketAccounts[legIndex] = value;
      return nextLegMarketAccounts;
    });
  }, []);

  return {
    legMarketAccounts,
    onLegMarketAccountsChange,
    resetLegMarketAccounts,
  };
}

function getSecurityLegMarketAccounts(security: Security | undefined): LegMarketAccounts {
  return security?.MultilegDetails?.Legs?.map(leg => leg.Markets?.map(market => market.MarketAccount) ?? []) ?? [];
}

/**
 * Market data cards store multileg UL leg market accounts in `selectedMarketAccounts` beside the
 * required synthetic `multileg/multileg` account. Use this when reading back only the editable UL leg
 * market accounts.
 */
export function getMultilegUnifiedLiquidityMarketAccounts(selectedMarketAccounts: readonly string[]): string[] {
  return selectedMarketAccounts.filter(marketAccount => marketAccount !== MULTILEG_MARKET_ACCOUNT);
}

/**
 * Persists multileg UL leg selections using the Market Data Card config shape. The synthetic multileg
 * market account must stay selected so the card remains a multileg card, while the preceding entries
 * represent the editable UL leg markets.
 */
export function toMultilegSelectedMarketAccounts(unifiedLiquidityMarketAccounts: readonly string[]): string[] {
  return [...unifiedLiquidityMarketAccounts, MULTILEG_MARKET_ACCOUNT];
}

export function shouldUpdateMultilegLegMarketAccounts(
  security: Security | undefined,
  legMarketAccounts: LegMarketAccounts
): boolean {
  if (!security || !isMultileg(security)) {
    return false;
  }

  return (
    security.MultilegDetails?.Legs.some((leg, index) => {
      const updatedMarkets = legMarketAccounts.at(index);

      return (
        updatedMarkets != null &&
        !areMarketAccountSelectionsEqual(leg.Markets?.map(market => market.MarketAccount) ?? [], updatedMarkets)
      );
    }) ?? false
  );
}

/**
 * Updates the persisted market accounts for regular multileg legs when the draft differs from
 * the instrument. Returns whether the multileg API was called.
 */
export async function updateMultilegLegMarkets({
  security,
  legMarketAccounts,
  orgApiEndpoint,
}: {
  security: Security | undefined;
  legMarketAccounts: LegMarketAccounts;
  orgApiEndpoint: string | undefined;
}): Promise<boolean> {
  const updateReqLegs = getMultilegLegMarketUpdateReqLegs(security, legMarketAccounts);

  if (
    !orgApiEndpoint ||
    !security ||
    updateReqLegs == null ||
    !shouldUpdateMultilegLegMarketAccounts(security, legMarketAccounts)
  ) {
    return false;
  }

  await updateMultilegLegMarketAccounts(security.Symbol, updateReqLegs, orgApiEndpoint);

  return true;
}

function getMultilegLegMarketUpdateReqLegs(
  security: Security | undefined,
  legMarketAccounts: LegMarketAccounts
): MultilegModel['ReqLegs'] | undefined {
  return security?.MultilegDetails?.Legs?.map((leg, index) => ({
    Symbol: leg.Symbol,
    Markets: legMarketAccounts.at(index) ?? leg.Markets?.map(market => market.MarketAccount) ?? [],
  }));
}

/**
 * Compares market account selections as an unordered selection set.
 *
 * API/instrument state and panel state can present the same selections in different orders; that
 * should not count as a user change. Duplicate entries still have to match because lengths and
 * sorted values are both compared.
 */
export function areMarketAccountSelectionsEqual(
  currentMarkets: readonly string[],
  updatedMarkets: readonly string[]
): boolean {
  const sortedCurrentMarkets = currentMarkets.toSorted();
  const sortedUpdatedMarkets = updatedMarkets.toSorted();

  return (
    sortedCurrentMarkets.length === sortedUpdatedMarkets.length &&
    sortedCurrentMarkets.every((market, index) => market === sortedUpdatedMarkets[index])
  );
}
